import { encounterCreatureField, inventoryField, itemsCol, layerHas } from './array-types';

type Readers = {
    entity(id: string): Promise<unknown>;
    /** `minReachFt` (when given) widens the actor's reach for this target only. */
    reachable(actorId: string, targetId: string, minReachFt?: number): Promise<boolean | null>;
    /** object_interaction_range_ft (Psion Telekinetic "Mental Grip", 2026-10-07): the actor's reach in feet for UNATTENDED objects —
     *  a loose floor row and a container standing on the floor. 0 / absent = the ordinary reach for everything. */
    objectReachFt?(actorId: string): Promise<number>;
};
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const record = (value: unknown): Record<string, unknown> | null =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const isContainer = (row: unknown): boolean => itemsCol(row, 'item_type') === 'container'
    || layerHas(row, 'carrying_capacity_pounds') || layerHas(row, 'carrying_capacity_volume');

/** Gate world sources/destinations before the inventory request. Carried bags use their holder. */
export async function inventoryMoveProximity(
    actorId: string, encounterId: string, inventoryId: string, destinationId: string,
    moveType: string, read: Readers,
): Promise<boolean | null> {
    const actor = upper(actorId), encounter = upper(encounterId);
    const destination = upper(destinationId);
    if (!encounter) return true; // inventory management outside an encounter has no world distance
    if (!actor) return null;
    try {
        const row = await read.entity(upper(inventoryId));
        if (!row || !inventoryField(row, 'inventory_id')) return null;
        const source = upper(inventoryField(row, 'possess_id'));
        const targets = new Set<string>();
        // Unattended objects (loose on the floor, or a container standing on it) — the targets object_interaction_range_ft reaches.
        // A give's receiving creature (below) is never one.
        const unattended = new Set<string>();
        if (source === encounter && ['move_inventory', 'equip_inventory'].includes(moveType)) {
            targets.add(upper(inventoryId));
            unattended.add(upper(inventoryId));
        }
        const containerIds = new Set<string>();
        if (source && source !== encounter && source !== actor) containerIds.add(source);
        if (destination && destination !== encounter && destination !== actor) {
            const dest = await read.entity(destination);
            if (!dest) return null;
            if (inventoryField(dest, 'inventory_id')) {
                containerIds.add(isContainer(dest) ? destination : upper(inventoryField(dest, 'possess_id')));
            }
        }
        for (const id of containerIds) {
            if (!id || id === encounter || id === actor) continue;
            const container = await read.entity(id);
            if (!container) return null;
            if (isContainer(container) && upper(inventoryField(container, 'possess_id')) === encounter) {
                targets.add(id);
                unattended.add(id);
            }
        }
        // D-020 (owner ruling 2026-09-30): a GIVE needs reach to the RECEIVING creature — the destination creature itself, or the creature
        // that carries a destination bag (at any depth). The actor's own tree and the floor are not a give. An unreadable link is unknown.
        if (destination && destination !== encounter && destination !== actor) {
            // The walk must END positively — the actor, the floor, or a creature (Codex review): an exhausted / cyclic chain, a missing
            // parent id or an unrecognized node is unknown (the server's own root walker allows 40 hops, so this one does too).
            let cursor = destination;
            let resolved = false;
            const seen = new Set<string>();
            for (let hop = 0; hop < 40 && !resolved; hop++) {
                if (!cursor || seen.has(cursor)) return null;
                if (cursor === encounter || cursor === actor) { resolved = true; break; }
                seen.add(cursor);
                const node = await read.entity(cursor);
                if (!node) return null;
                if (inventoryField(node, 'inventory_id')) { cursor = upper(inventoryField(node, 'possess_id')); continue; }
                if (upper(encounterCreatureField(record(node)?.encounter_creatures, 'encounter_creature_id')) !== cursor) return null;
                targets.add(cursor);
                unattended.delete(cursor);
                resolved = true;
            }
            if (!resolved) return null;
        }
        const objectReach = unattended.size > 0 && read.objectReachFt ? Math.max(0, Number(await read.objectReachFt(actor)) || 0) : 0;
        for (const id of targets) {
            const within = objectReach > 0 && unattended.has(id) ? await read.reachable(actor, id, objectReach) : await read.reachable(actor, id);
            if (within !== true) return within;
        }
        return true;
    } catch {
        return null;
    }
}
